import React, { useEffect, useState } from 'react';
import { Plus, Edit2, Trash2, Save, RefreshCw, Wand2, ChevronDown, Loader2 } from 'lucide-react';
import { adminApi } from '../../api';
import { PageHeader, Table, Modal, Field, Input, Select, I18nInputs } from './AdminUI';

const EMPTY = { city_slug: 'desenzano', category: 'general', name: {it:'',en:''}, phone: '', icon: 'Phone', address: '', order: 0 };
const ICONS = ['AlertTriangle','Heart','Shield','Flame','Cross','Anchor','Phone','Ambulance','Stethoscope'];

export default function AdminEmergency() {
  const [items, setItems] = useState([]);
  const [cities, setCities] = useState([]);
  const [editing, setEditing] = useState(null);
  const [syncingCity, setSyncingCity] = useState('');
  const [syncBusy, setSyncBusy] = useState(false);
  const load = () => Promise.all([adminApi.emergency(), adminApi.cities()]).then(([e, c]) => { setItems(e); setCities(c); });
  useEffect(() => { load(); }, []);

  const save = async (e) => {
    e.preventDefault();
    try {
      const payload = { ...editing, order: Number(editing.order) || 0 };
      if (editing.id) await adminApi.updateEmergency(editing.id, payload);
      else await adminApi.createEmergency(payload);
      setEditing(null); load();
    } catch (err) { alert(err.response?.data?.detail || 'Errore'); }
  };

  const del = async (row) => { if (!window.confirm(`Eliminare?`)) return; await adminApi.deleteEmergency(row.id); load(); };

  const syncFromCountry = async (mode) => {
    if (!syncingCity) return;
    const city = cities.find(c => c.slug === syncingCity);
    const label = mode === 'replace'
      ? `⚠️ SOSTITUIRE tutti i contatti di ${city?.name || syncingCity} con i default del paese "${city?.country || 'IT'}"?\n\nTutte le modifiche manuali verranno CANCELLATE.`
      : `Aggiungere solo i numeri MANCANTI a ${city?.name || syncingCity} dal paese "${city?.country || 'IT'}"?\n\nI contatti esistenti verranno preservati.`;
    if (!window.confirm(label)) return;
    setSyncBusy(true);
    try {
      const r = await adminApi.emergencySyncCountry(syncingCity, mode);
      const msg = mode === 'replace'
        ? `✅ Sostituzione completata: ${r.deleted} eliminati, ${r.inserted} inseriti dal paese ${r.country}`
        : `✅ Fill completato: ${r.inserted} nuovi inseriti (${r.skipped_existing} già presenti) — paese ${r.country}`;
      alert(msg);
      await load();
    } catch (err) {
      alert(err.response?.data?.detail || 'Errore');
    } finally { setSyncBusy(false); }
  };

  const applyLocalTemplates = async () => {
    if (!syncingCity) return;
    setSyncBusy(true);
    try {
      const r = await adminApi.emergencyApplyLocalTemplates(syncingCity);
      alert(`✅ Servizi locali applicati: ${r.inserted} inseriti, ${r.updated} aggiornati, ${r.skipped_no_number} saltati (nessun numero configurato per questa città).`);
      await load();
    } catch (err) {
      alert(err.response?.data?.detail || 'Errore');
    } finally { setSyncBusy(false); }
  };

  return (
    <div>
      <PageHeader
        title="Emergenze"
        subtitle={`${items.length} contatti`}
        action={<button onClick={() => setEditing({...EMPTY})} className="btn-primary flex items-center gap-2" data-testid="new-emergency-btn"><Plus size={16}/> Nuovo</button>}
      />

      {/* --- Sync per città --- */}
      <div className="mb-4 rounded-lg border border-amber-200 bg-amber-50/60 p-4" data-testid="emergency-sync-panel">
        <div className="text-sm font-semibold text-amber-900 mb-2 flex items-center gap-2">
          <RefreshCw size={14}/> Ripristina numeri di emergenza dal paese
        </div>
        <p className="text-xs text-amber-800/80 mb-3">
          Utile dopo aver cambiato il paese di una città: rigenera i default nazionali senza toccare manualmente le altre. La modalità <strong>Aggiungi mancanti</strong> è sicura; la modalità <strong>Sostituisci tutto</strong> cancella prima i contatti esistenti.
        </p>
        <div className="flex flex-wrap gap-2 items-center">
          <select
            value={syncingCity}
            onChange={e => setSyncingCity(e.target.value)}
            data-testid="emergency-sync-city-select"
            className="px-3 py-1.5 text-sm rounded-md border border-amber-300 bg-white"
          >
            <option value="">— Seleziona città —</option>
            {cities.map(c => (
              <option key={c.slug} value={c.slug}>
                {c.name || c.slug} ({(c.country || 'IT').toUpperCase()})
              </option>
            ))}
          </select>
          <button
            type="button"
            onClick={() => syncFromCountry('fill')}
            disabled={!syncingCity || syncBusy}
            data-testid="emergency-sync-fill-btn"
            className="inline-flex items-center gap-1.5 px-3 py-1.5 text-sm rounded-md bg-emerald-600 hover:bg-emerald-700 text-white font-semibold disabled:opacity-50"
          >
            {syncBusy ? <Loader2 size={14} className="animate-spin"/> : <Plus size={14}/>}
            Aggiungi mancanti
          </button>
          <button
            type="button"
            onClick={() => syncFromCountry('replace')}
            disabled={!syncingCity || syncBusy}
            data-testid="emergency-sync-replace-btn"
            className="inline-flex items-center gap-1.5 px-3 py-1.5 text-sm rounded-md bg-red-600 hover:bg-red-700 text-white font-semibold disabled:opacity-50"
          >
            {syncBusy ? <Loader2 size={14} className="animate-spin"/> : <RefreshCw size={14}/>}
            Sostituisci tutto
          </button>
          <button
            type="button"
            onClick={applyLocalTemplates}
            disabled={!syncingCity || syncBusy}
            data-testid="emergency-apply-local-btn"
            className="inline-flex items-center gap-1.5 px-3 py-1.5 text-sm rounded-md bg-indigo-600 hover:bg-indigo-700 text-white font-semibold disabled:opacity-50"
          >
            {syncBusy ? <Loader2 size={14} className="animate-spin"/> : <Wand2 size={14}/>}
            Applica servizi locali
          </button>
        </div>
      </div>

      {/* --- Local emergency templates editor --- */}
      <LocalTemplatesPanel cities={cities} />

      <Table
        columns={[
          { key: 'city_slug', label: 'Città' },
          { key: 'category', label: 'Categoria', render: r => <span className="text-xs font-mono bg-gray-100 px-2 py-0.5 rounded">{r.category}</span> },
          { key: 'name', label: 'Nome (IT)', render: r => <span className="font-semibold">{r.name?.it || r.name?.en}</span> },
          { key: 'phone', label: 'Telefono' },
          { key: 'address', label: 'Indirizzo' }
        ]}
        rows={items}
        actions={row => (
          <div className="flex gap-2 justify-end">
            <button onClick={() => setEditing({...row})} className="p-1.5 hover:bg-gray-100 rounded text-blue-600"><Edit2 size={15}/></button>
            <button onClick={() => del(row)} className="p-1.5 hover:bg-gray-100 rounded text-red-600"><Trash2 size={15}/></button>
          </div>
        )}
      />
      <Modal open={!!editing} onClose={() => setEditing(null)} title={editing?.id ? 'Modifica contatto' : 'Nuovo contatto emergenza'}>
        {editing && (
          <form onSubmit={save}>
            <div className="grid grid-cols-2 gap-3">
              <Field label="Città"><Select value={editing.city_slug} onChange={e => setEditing({...editing, city_slug: e.target.value})}>{cities.map(c => <option key={c.slug} value={c.slug}>{c.name}</option>)}</Select></Field>
              <Field label="Categoria"><Input value={editing.category} onChange={e => setEditing({...editing, category: e.target.value})} placeholder="general / medical / police / fire / hospital / coastguard" /></Field>
            </div>
            <Field label="Nome per lingua"><I18nInputs value={editing.name} onChange={v => setEditing({...editing, name: v})} /></Field>
            <div className="grid grid-cols-2 gap-3">
              <Field label="Numero telefono"><Input required value={editing.phone} onChange={e => setEditing({...editing, phone: e.target.value})} placeholder="112 o +39 030 ..." /></Field>
              <Field label="Icona"><Select value={editing.icon} onChange={e => setEditing({...editing, icon: e.target.value})}>{ICONS.map(i => <option key={i} value={i}>{i}</option>)}</Select></Field>
            </div>
            <Field label="Indirizzo"><Input value={editing.address || ''} onChange={e => setEditing({...editing, address: e.target.value})} /></Field>
            <Field label="Ordine"><Input type="number" value={editing.order} onChange={e => setEditing({...editing, order: e.target.value})} /></Field>
            <div className="flex justify-end gap-2 mt-6">
              <button type="button" onClick={() => setEditing(null)} className="px-4 py-2 border border-gray-300 rounded-md hover:bg-gray-50">Annulla</button>
              <button type="submit" className="btn-primary flex items-center gap-2"><Save size={16}/> Salva</button>
            </div>
          </form>
        )}
      </Modal>
    </div>
  );
}


/**
 * Local Emergency Templates editor. Manages a shared JSON (in settings) that
 * lists Comune-level services (Guardia Medica, Protezione Civile, ...) and
 * their phone numbers per-city. Admin edits once here; the "Applica servizi
 * locali" button on top pushes the numbers into a specific city as real
 * EmergencyContact rows.
 */
function LocalTemplatesPanel({ cities }) {
  const [open, setOpen] = useState(false);
  const [templates, setTemplates] = useState({});
  const [saving, setSaving] = useState(false);
  const [loaded, setLoaded] = useState(false);

  useEffect(() => {
    if (!open || loaded) return;
    adminApi.emergencyGetLocalTemplates().then(r => {
      setTemplates(r.templates || {});
      setLoaded(true);
    }).catch(() => setLoaded(true));
  }, [open, loaded]);

  const save = async () => {
    setSaving(true);
    try {
      await adminApi.emergencySaveLocalTemplates(templates);
      alert('✅ Template salvati');
    } catch (e) {
      alert(e.response?.data?.detail || 'Errore');
    } finally { setSaving(false); }
  };

  const setNumber = (tmplKey, citySlug, val) => {
    setTemplates(prev => ({
      ...prev,
      [tmplKey]: {
        ...prev[tmplKey],
        numbers: { ...(prev[tmplKey]?.numbers || {}), [citySlug]: val }
      }
    }));
  };

  const setLabel = (tmplKey, lang, val) => {
    setTemplates(prev => ({
      ...prev,
      [tmplKey]: { ...prev[tmplKey], label: { ...(prev[tmplKey]?.label || {}), [lang]: val } }
    }));
  };

  const addTemplate = () => {
    const key = prompt('Chiave nuovo template (solo lettere e underscore, es. carabinieri_locali):');
    if (!key || !/^[a-z_][a-z0-9_]*$/.test(key)) return alert('Chiave non valida');
    if (templates[key]) return alert('Chiave già esistente');
    setTemplates(prev => ({
      ...prev,
      [key]: { label: { it: '', en: '' }, category: 'general', icon: 'Phone', numbers: {} }
    }));
  };

  const removeTemplate = (key) => {
    if (!window.confirm(`Rimuovere il template "${key}"?`)) return;
    setTemplates(prev => {
      const next = { ...prev };
      delete next[key];
      return next;
    });
  };

  return (
    <div className="mb-4 rounded-lg border border-indigo-200 bg-indigo-50/60" data-testid="local-templates-panel">
      <button
        type="button"
        onClick={() => setOpen(o => !o)}
        className="w-full flex items-center justify-between px-4 py-3 text-left"
        data-testid="local-templates-toggle"
      >
        <div>
          <div className="font-semibold text-indigo-900 text-sm flex items-center gap-2">
            <Wand2 size={14}/> Servizi locali per Comune (Guardia Medica, Protezione Civile...)
          </div>
          <p className="text-xs text-indigo-800/80 mt-0.5">
            Un'unica lista modificabile con i numeri locali. Poi il bottone "Applica servizi locali" li inserisce nel Comune scelto.
          </p>
        </div>
        <ChevronDown size={18} className={`text-indigo-700 transition-transform ${open ? 'rotate-180' : ''}`} />
      </button>

      {open && (
        <div className="px-4 pb-4 space-y-4">
          {Object.entries(templates).map(([key, t]) => (
            <div key={key} className="bg-white rounded-md border border-indigo-200 p-3" data-testid={`local-tmpl-${key}`}>
              <div className="flex items-center justify-between mb-2">
                <div className="font-mono text-xs bg-indigo-100 text-indigo-800 px-2 py-0.5 rounded">{key}</div>
                <button onClick={() => removeTemplate(key)} className="p-1 text-red-600 hover:bg-red-50 rounded" data-testid={`local-tmpl-remove-${key}`}><Trash2 size={13}/></button>
              </div>
              <div className="mb-3">
                <div className="text-[10px] font-semibold uppercase text-slate-500 mb-1.5">Nome template (per lingua)</div>
                <I18nInputs
                  value={t.label || {}}
                  onChange={(v) => setTemplates(prev => ({ ...prev, [key]: { ...prev[key], label: v } }))}
                  fallbackText={t.label?.it || ''}
                />
              </div>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-2 mb-3">
                <label className="block">
                  <span className="text-[10px] font-semibold uppercase text-slate-500">Categoria</span>
                  <select
                    value={t.category || 'general'}
                    onChange={e => setTemplates(prev => ({...prev, [key]: {...prev[key], category: e.target.value}}))}
                    className="w-full text-sm px-2 py-1 rounded border border-slate-300"
                  >
                    <option value="general">general</option>
                    <option value="medical">medical</option>
                    <option value="police">police</option>
                    <option value="fire">fire</option>
                    <option value="hospital">hospital</option>
                    <option value="coastguard">coastguard</option>
                  </select>
                </label>
                <label className="block">
                  <span className="text-[10px] font-semibold uppercase text-slate-500">Icona</span>
                  <select
                    value={t.icon || 'Phone'}
                    onChange={e => setTemplates(prev => ({...prev, [key]: {...prev[key], icon: e.target.value}}))}
                    className="w-full text-sm px-2 py-1 rounded border border-slate-300"
                  >
                    {ICONS.map(i => <option key={i} value={i}>{i}</option>)}
                  </select>
                </label>
              </div>
              <div>
                <div className="text-[10px] font-semibold uppercase text-slate-500 mb-1">Numeri per città</div>
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-2">
                  {cities.map(c => (
                    <label key={c.slug} className="block">
                      <span className="text-[11px] text-slate-600">{c.name} <span className="text-slate-400">({c.slug})</span></span>
                      <input
                        type="text"
                        value={(t.numbers || {})[c.slug] || ''}
                        onChange={e => setNumber(key, c.slug, e.target.value)}
                        placeholder="es. 030 999 8666"
                        className="w-full text-sm px-2 py-1 rounded border border-slate-300"
                        data-testid={`local-tmpl-${key}-${c.slug}`}
                      />
                    </label>
                  ))}
                </div>
              </div>
            </div>
          ))}
          <div className="flex justify-between items-center gap-2 pt-2">
            <button
              type="button"
              onClick={addTemplate}
              data-testid="local-tmpl-add"
              className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs rounded-md border border-indigo-300 bg-white hover:bg-indigo-50 text-indigo-800 font-semibold"
            >
              <Plus size={12}/> Aggiungi template
            </button>
            <button
              type="button"
              onClick={save}
              disabled={saving}
              data-testid="local-tmpl-save"
              className="inline-flex items-center gap-1.5 px-4 py-1.5 text-sm rounded-md bg-indigo-600 hover:bg-indigo-700 text-white font-semibold disabled:opacity-50"
            >
              {saving ? <Loader2 size={14} className="animate-spin"/> : <Save size={14}/>} Salva template
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
